import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from "./config";
import '../css/DashboardAdmin.css';
import socket from "../socket/socket";
import { openAdminPlayerEditor, getPlayIntensity } from '../utils/playIntensity';
function Dashboard() {
  const navigate = useNavigate();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem('user')),
    []
  );

  const [menu, setMenu] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [checkingOutUserId, setCheckingOutUserId] = useState(null);
  const selectedGroupId = selectedGroup?.id;

  /* ===============================
     AUTH CHECK
  =============================== */
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  /* ===============================
     LOAD GROUPS
  =============================== */
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    const res = await fetch(`${API_BASE}/api/groups`);
    const data = await res.json();
    setGroups(data);
  };

  const fetchMembers = async (groupId) => {
    if (!groupId) return;

    const res = await fetch(`${API_BASE}/api/groups/${groupId}/members`);
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.message || data.error || 'โหลดรายชื่อสมาชิกไม่สำเร็จ');
    }

    setMembers(Array.isArray(data) ? data : []);
  };

  /* ===============================
     SOCKET REALTIME (ADMIN)
  =============================== */
  useEffect(() => {

    if (!selectedGroupId) return;

    const groupId = selectedGroupId;

    const handleConnect = () => {
      console.log("📡 Admin join-group:", groupId);
      socket.emit("join-group", Number(groupId));

      fetchMembers(groupId).catch(error => {
        console.error("LOAD MEMBERS AFTER SOCKET CONNECT ERROR:", error);
      });
    };

    socket.on("connect", handleConnect);

    if (socket.connected) {
      handleConnect();
    }

    const handleMemberUpdate = (data = {}) => {
      console.log("🔥 Admin received:", data);

      const eventGroupId = data.groupId ?? data.group_id;

      // บาง event อาจไม่ได้ส่ง groupId มา ให้โหลดกลุ่มปัจจุบันได้เลย
      if (
        eventGroupId !== undefined &&
        eventGroupId !== null &&
        Number(eventGroupId) !== Number(groupId)
      ) {
        return;
      }

      fetchMembers(groupId).catch(error => {
        console.error("LOAD MEMBERS AFTER SOCKET EVENT ERROR:", error);
      });
    };

    socket.on("member-updated", handleMemberUpdate);
    socket.on("member-checkout", handleMemberUpdate);
    socket.on("member-joined", handleMemberUpdate);
    socket.on("player-removed", handleMemberUpdate);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("member-updated", handleMemberUpdate);
      socket.off("member-checkout", handleMemberUpdate);
      socket.off("member-joined", handleMemberUpdate);
      socket.off("player-removed", handleMemberUpdate);
    };

  }, [selectedGroupId]);

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    fetchMembers(group.id).catch(error => {
      alert(`❌ ${error.message}`);
    });
    setMenu('members');
  };

  const handleAdminCheckout = async (userId) => {
    if (!selectedGroup || checkingOutUserId !== null) return;
    if (!window.confirm('ยืนยัน Check-out ผู้ใช้นี้?')) return;

    try {
      setCheckingOutUserId(userId);

      const res = await fetch(`${API_BASE}/api/checkin/admin/${userId}`, {
        method: 'DELETE'
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || data.error || 'Check-out ไม่สำเร็จ');
      }

      // อัปเดตหน้าที่กดทันที ไม่ต้องรอ Socket
      setMembers(currentMembers =>
        currentMembers.filter(member => Number(member.user_id) !== Number(userId))
      );

      // ดึงจากฐานข้อมูลซ้ำเพื่อยืนยัน state ที่ถูกต้อง
      await fetchMembers(selectedGroup.id);
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setCheckingOutUserId(null);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (!user || user.role !== 'admin') return null;

  return (
 

    <div className="admin-layout">

      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <h3>🏸 Admin</h3>

        <button
          className={menu === 'groups' ? 'active' : ''}
          onClick={() => setMenu('groups')}
        >
          📋 Groups
        </button>

        <button
          disabled={!selectedGroup}
          className={menu === 'members' ? 'active' : ''}
          onClick={() => setMenu('members')}
        >
          👥 Members
        </button>

        <div className="spacer" />

        <button className="logout-btn" onClick={logout}>
          🚪 Logout
        </button>
      </aside>

      {/* MAIN */}
      <main className="admin-main">

        {menu === 'groups' && (
          <>
            <h2>Group ทั้งหมด</h2>

            <div className="card-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Group Name</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => (
                    <tr key={g.id}>
                      <td>{g.id}</td>
                      <td>{g.Group_Name}</td>
                      <td>
                        <span className={`status ${g.Group_Status}`}>
                          {g.Group_Status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="action-btn"
                          onClick={() => handleSelectGroup(g)}
                        >
                          ดูสมาชิก
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {menu === 'members' && selectedGroup && (
          <>
            <div className="member-header">
              <h2>สมาชิก: {selectedGroup.Group_Name}</h2>
              <button onClick={() => setMenu('groups')} className="back-btn">
                ⬅ กลับ
              </button>
            </div>

            <div className="card-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Rank</th>
                    <th>Point</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.user_id}>
                      <td><button type="button" className="admin-inline-player-name" onClick={()=>openAdminPlayerEditor(m)}>{m.name}</button></td>
                      <td>{m.level_rank} · {getPlayIntensity(m.play_intensity).icon} {getPlayIntensity(m.play_intensity).label}</td>
                      <td>{m.point}</td>
                      <td>
                        <button
                          className="danger-btn"
                          disabled={
                            Number(checkingOutUserId) === Number(m.user_id)
                          }
                          onClick={() => handleAdminCheckout(m.user_id)}
                        >
                          {Number(checkingOutUserId) === Number(m.user_id)
                            ? 'กำลัง Check-out...'
                            : 'Check-out'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

      </main>

    </div>
  );
}

export default Dashboard;
